import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styles from './Tooltip.module.scss';

/* ────────────────────────────────────────────────────────────
 * Tooltip — contextual help bubble for a single trigger.
 *
 *   <Tooltip content="Groups raw security tags into one team.">
 *     <IconButton icon={<HelpCircle />} aria-label="What is this?" />
 *   </Tooltip>
 *
 * Opens on hover AND on keyboard focus, closes on Esc — so the
 * help is reachable without a pointer. The bubble is portalled to
 * document.body (same escape-hatch as Select / MultiSelect) so it
 * is never clipped by a panel's overflow, and it is wired to the
 * trigger via aria-describedby rather than the native `title`
 * attribute, which screen readers announce inconsistently.
 *
 * Motion: fade + 2px rise on --ease-out-quart (MOTION.md §4.12).
 * ──────────────────────────────────────────────────────────── */

// Distance between the trigger edge and the bubble, in px. Kept as a
// number (not a token) because it feeds getBoundingClientRect math.
const OFFSET = 8;
// Minimum breathing room between the bubble and the viewport edge.
const VIEWPORT_PAD = 8;

const Tooltip = ({ content, placement, disabled, children, className }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, side: placement });

  const triggerRef = useRef(null);
  const bubbleRef = useRef(null);
  const describedById = useId();

  const show = useCallback(() => {
    if (!disabled && content) setOpen(true);
  }, [disabled, content]);

  const hide = useCallback(() => setOpen(false), []);

  // Screen readers announce the description of the FOCUSED element — the
  // button, switch or (i) inside this wrapper, never the wrapper itself. So
  // the text is kept in a hidden element that always exists (a description
  // must be there at the moment focus lands, not after the bubble opens),
  // and the first focusable element inside is pointed at it. Any description
  // it already had is kept.
  const hasDescription = !disabled && content !== null && content !== undefined && content !== '';
  useLayoutEffect(() => {
    const wrapper = triggerRef.current;
    if (!wrapper || !hasDescription) return undefined;
    const target = wrapper.querySelector(
      'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!target) return undefined;
    const before = target.getAttribute('aria-describedby');
    const ids = (before || '').split(/\s+/).filter(Boolean);
    if (!ids.includes(describedById)) {
      target.setAttribute('aria-describedby', [...ids, describedById].join(' '));
    }
    return () => {
      if (before) target.setAttribute('aria-describedby', before);
      else target.removeAttribute('aria-describedby');
    };
  }, [describedById, hasDescription, children]);

  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    const bubble = bubbleRef.current;
    if (!trigger || !bubble) return;

    const t = trigger.getBoundingClientRect();
    const b = bubble.getBoundingClientRect();

    // Flip to the opposite side when the preferred one would overflow.
    let side = placement;
    if (side === 'top' && t.top - b.height - OFFSET < VIEWPORT_PAD) side = 'bottom';
    else if (
      side === 'bottom' &&
      t.bottom + b.height + OFFSET > window.innerHeight - VIEWPORT_PAD
    ) {
      side = 'top';
    }

    const top =
      side === 'top' ? t.top - b.height - OFFSET : t.bottom + OFFSET;

    const maxLeft = window.innerWidth - b.width - VIEWPORT_PAD;
    const left = Math.min(
      Math.max(VIEWPORT_PAD, t.left + t.width / 2 - b.width / 2),
      Math.max(VIEWPORT_PAD, maxLeft)
    );

    setPosition({ top, left, side });
  }, [placement]);

  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition, content]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') hide();
    };
    // `true` — catch scrolls in any ancestor, not just the window.
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, reposition, hide]);

  const wrapperClasses = [styles.trigger, className || '']
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <span
        ref={triggerRef}
        className={wrapperClasses}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
        {hasDescription && (
          <span id={describedById} hidden>
            {content}
          </span>
        )}
      </span>

      {open &&
        createPortal(
          <div
            ref={bubbleRef}
            role="tooltip"
            className={[styles.bubble, styles[position.side]].join(' ')}
            style={{ top: position.top, left: position.left }}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
};

Tooltip.propTypes = {
  content: PropTypes.node,
  placement: PropTypes.oneOf(['top', 'bottom']),
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

Tooltip.defaultProps = {
  content: null,
  placement: 'top',
  disabled: false,
  className: '',
};

export default Tooltip;
